import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.svg';
import { launchSuiteApp } from '../utils/launchSuiteApp';
import { BrandBackdrop } from '../components/BrandBackdrop';
import { AppLogo } from './onboarding/AppLogo';

/**
 * WorkspaceHome — the Central `/home` route.
 *
 * This is the Central-hosted landing / recovery / launcher shell served at
 * `<central>/home`. It is NOT the Home suite app (`appId: 'home'`, served at
 * its own `SUITE_APP_HOME_URL`, e.g. `<home>/home`) — the two are distinct.
 * This shell lists the workspace's suite apps and lets the user launch into
 * them; the actual destination experiences are the suite apps themselves.
 *
 * Normal users finishing onboarding (or logging back in) land on this route,
 * not on the admin control plane at `/` (which is admin-gated and 403s for
 * non-admins).
 *
 * Data source is the session-authed `GET /api/workspace/current`. The
 * endpoint's `status` drives what this route does:
 *  - no-workspace          → redirect to the workspace picker.
 *  - onboarding-incomplete → render the workspace's apps with a "Finish
 *                            setting up" banner. Never a redirect: an
 *                            existing account is not forced back into
 *                            onboarding (owner report, 2026-10-08).
 *  - provisioned           → render the workspace's launchable suite apps.
 *  - empty                 → friendly "still settling" state with refresh.
 *
 * On a hard fetch failure the route shows a recoverable `degraded` state.
 * A 401 means there is no session at all → redirect to login. None of these
 * paths dead-end the user.
 */

interface HomeApp {
  appId: string;
  displayName: string;
  /**
   * Phase 2.x-c — Application Registry v2 metadata, mode-resolved on
   * the backend (business vs personal per workspace.entryType). Optional
   * so the tile degrades gracefully when the registry lacks copy or the
   * backend predates PR-A.
   */
  subtitle?: string;
  description?: string;
  order: number;
  /** External suite-app launch URL, or null when not resolvable. */
  launchUrl: string | null;
  /** True when the app has a usable launchUrl. */
  launchable: boolean;
  /** H-2A: true → launch via the OIDC flow; false/absent → bare link. */
  oidcLaunch?: boolean;
  /** The app's logo — the same file the app switcher shows. */
  iconUrl?: string | null;
}

interface DefaultApp {
  appId: string;
  displayName: string;
  launchUrl: string | null;
  launchable: boolean;
  oidcLaunch?: boolean;
}

type HomeStatus = 'loading' | 'provisioned' | 'incomplete' | 'empty' | 'degraded' | 'closed';

/** The workspace fields `/api/workspace/current` reports for a resume decision. */
interface IncompleteWorkspace {
  /** The mode the owner CHOSE; null until chosen (newer backends always send it). */
  onboardingMode?: string | null;
  /** entryType-derived mode (defaults to business when never chosen). */
  mode?: string | null;
  entryType?: string | null;
  type?: string | null;
}

const isFlowMode = (m: unknown): m is 'business' | 'personal' => m === 'business' || m === 'personal';

/**
 * Where to resume an onboarding-incomplete workspace (ONB-05).
 *
 * From the workspace itself — never a hard-coded Business, which put a
 * personal workspace into the business flow. The chosen `onboardingMode` wins;
 * when the backend says none was chosen yet, the Personal/Business chooser is
 * the resume point (it reuses THIS workspace, and nothing is in progress to
 * lose). Older payloads without that field fall back to the workspace's
 * mode/entryType/type.
 */
export function resumeOnboardingPath(ws: IncompleteWorkspace | null | undefined): string {
  if (ws && 'onboardingMode' in ws) {
    return isFlowMode(ws.onboardingMode) ? `/start/${ws.onboardingMode}` : '/onboarding/new';
  }
  const derived = isFlowMode(ws?.mode)
    ? ws!.mode
    : isFlowMode(ws?.entryType)
      ? ws!.entryType
      : ws?.type === 'personal'
        ? 'personal'
        : ws?.type
          ? 'business'
          : null;
  return derived ? `/start/${derived}` : '/onboarding/new';
}

export function WorkspaceHomePage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<HomeStatus>('loading');
  const [workspaceName, setWorkspaceName] = useState<string>('your workspace');
  const [apps, setApps] = useState<HomeApp[]>([]);
  const [defaultApp, setDefaultApp] = useState<DefaultApp | null>(null);
  // Where "Finish setting up" resumes an unfinished workspace.
  const [finishSetupPath, setFinishSetupPath] = useState<string>('/onboarding/new');

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const res = await fetch('/api/workspace/current', { credentials: 'include' });

      // No session at all — send the user to sign in. REPLACE, not push: a
      // pushed /home entry re-detects the dead session on Back and bounces to
      // sign-in again, trapping the user (V1-CENTRAL-LOGIN, runtime-proven).
      if (res.status === 401) {
        navigate('/auth/login', { replace: true });
        return;
      }
      if (!res.ok) {
        setStatus('degraded');
        return;
      }

      const data = await res.json();
      if (!data?.ok) {
        setStatus('degraded');
        return;
      }

      switch (data.status) {
        case 'password-change-required':
          // An admin-issued temporary password: choose a new one first, then
          // come back here. REPLACE, for the same reason as the 401 above.
          navigate(`/auth/change-password?continue=${encodeURIComponent('/home')}`, { replace: true });
          return;
        case 'no-workspace':
          // Session has no workspace bound — route to the picker, never
          // to the admin control plane.
          navigate('/auth/workspace');
          return;
        case 'closed-workspace':
          // The bound workspace is closed — show a terminal "unavailable"
          // screen with a path to pick an active workspace. Deliberately
          // not an auto-redirect, to avoid re-selecting the same closed
          // workspace and looping.
          setStatus('closed');
          return;
        case 'onboarding-incomplete': {
          // The workspace never finished onboarding. Show it — with whatever
          // apps it can already open — and OFFER to finish setup; never send
          // the user there. Finish setup resumes THIS workspace via
          // /start/{mode}, or the chooser when no mode was ever chosen.
          setWorkspaceName(data.workspace?.name?.trim() || 'your workspace');
          setApps(Array.isArray(data.apps) ? data.apps : []);
          setDefaultApp(data.defaultApp ?? null);
          setFinishSetupPath(resumeOnboardingPath(data.workspace));
          setStatus('incomplete');
          return;
        }
        case 'provisioned':
        case 'empty': {
          const resolvedApps: HomeApp[] = Array.isArray(data.apps) ? data.apps : [];
          setWorkspaceName(data.workspace?.name?.trim() || 'your workspace');
          setApps(resolvedApps);
          setDefaultApp(data.defaultApp ?? null);
          setStatus(data.status);
          return;
        }
        default:
          setStatus('degraded');
      }
    } catch {
      setStatus('degraded');
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // The recommended app leads in its own card; every other app is in the grid,
  // so each app appears exactly once.
  const featured = defaultApp ? apps.find((a) => a.appId === defaultApp.appId) ?? null : null;
  const others = featured ? apps.filter((a) => a.appId !== featured.appId) : apps;

  return (
    <BrandBackdrop>
      <header className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-6 sm:py-6">
        <img src={logo} alt="LevelUp" className="h-7 drop-shadow sm:h-9" />
        <Link
          to="/settings"
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-3.5 py-2 text-sm font-medium text-white backdrop-blur transition-colors hover:bg-white/[0.12] focus:outline-none focus:ring-2 focus:ring-emerald-400"
        >
          <GearIcon className="h-4 w-4 text-emerald-300" />
          <span className="hidden sm:inline">Workspace settings</span>
          <span className="sm:hidden">Settings</span>
        </Link>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 pb-16 sm:px-6">
        {status === 'loading' && (
          <StateCard>
            <div role="status" className="flex flex-col items-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
              <p className="mt-4 text-gray-500">Loading your workspace…</p>
            </div>
          </StateCard>
        )}

        {(status === 'provisioned' || status === 'incomplete') && (
          <div>
            <div className="pt-2 sm:pt-6">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-emerald-300/90">
                {status === 'incomplete' && apps.length === 0
                  ? 'Almost there'
                  : `${apps.length} ${apps.length === 1 ? 'app' : 'apps'} switched on`}
              </p>
              <h1 className="mt-3 text-2xl font-semibold tracking-tight text-white sm:text-4xl">
                Welcome to {workspaceName}
              </h1>
              <p className="mt-2 text-slate-300 sm:text-lg">
                {status === 'incomplete' && apps.length === 0
                  ? 'Finish setting up to choose the apps you want.'
                  : 'Your workspace is ready. Open an app to get started.'}
              </p>
            </div>

            {status === 'incomplete' && (
              <div className="mt-6 flex flex-col gap-4 rounded-3xl border border-emerald-400/25 bg-gradient-to-r from-emerald-500/15 via-white/[0.05] to-white/[0.02] p-5 backdrop-blur sm:mt-8 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                <div className="flex items-start gap-3.5">
                  <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-emerald-400/15 text-emerald-300 ring-1 ring-emerald-400/30">
                    <SparkIcon className="h-5 w-5" />
                  </span>
                  <div>
                    <h2 className="font-semibold text-white">Finish setting up your workspace</h2>
                    <p className="mt-0.5 text-sm text-slate-300">
                      Pick what you want LevelUp to help with and we'll switch on the right apps. It takes about a minute.
                    </p>
                  </div>
                </div>
                <Link
                  to={finishSetupPath}
                  className="inline-flex flex-none items-center justify-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-slate-950 shadow-lg shadow-emerald-500/25 transition-colors hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                >
                  Finish setup
                  <ArrowIcon className="h-4 w-4" />
                </Link>
              </div>
            )}

            {featured && (
              <div className="mt-6 sm:mt-8">
                <AppTile app={featured} featured />
              </div>
            )}

            <div className="mt-4 rounded-3xl bg-white p-4 shadow-2xl shadow-black/40 ring-1 ring-black/5 sm:mt-6 sm:p-6">
              {others.length > 0 && (
                <>
                  <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 sm:mb-4 sm:text-xs">
                    {featured ? 'More in your workspace' : 'Your apps'}
                  </h2>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {others.map((app) => (
                      <AppTile key={app.appId} app={app} />
                    ))}
                  </div>
                </>
              )}

              <div className={others.length > 0 ? 'mt-6 border-t border-gray-100 pt-5' : ''}>
                <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 sm:text-xs">
                  Next steps
                </h2>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <NextStep to="/settings" title="Review your workspace settings" sub="Name, logo and details" icon={<GearIcon className="h-5 w-5" />} />
                  <NextStep to="/settings/members" title="Invite people" sub="Bring your team or family in" icon={<PeopleIcon className="h-5 w-5" />} />
                  <NextStep to="/settings/billing" title="Plan & billing" sub="Your plan, credits and invoices" icon={<CardIcon className="h-5 w-5" />} />
                </div>
              </div>
            </div>
          </div>
        )}

        {status === 'empty' && (
          <StateCard>
            <h1 className="text-lg font-semibold text-gray-900">{workspaceName} is set up</h1>
            <p className="mt-2 text-sm text-gray-500">
              We're still preparing your apps. This usually only takes a moment.
            </p>
            <button type="button" onClick={() => load()} className={PRIMARY_BUTTON}>
              Refresh
            </button>
          </StateCard>
        )}

        {status === 'closed' && (
          <StateCard>
            <h1 className="text-lg font-semibold text-gray-900">This workspace is closed</h1>
            <p className="mt-2 text-sm text-gray-500">
              The workspace you were in has been closed and is no longer
              available. Choose another workspace to continue.
            </p>
            <Link to="/auth/workspace" className={`${PRIMARY_BUTTON} inline-block`}>
              Choose a workspace
            </Link>
          </StateCard>
        )}

        {status === 'degraded' && (
          <StateCard>
            <h1 className="text-lg font-semibold text-gray-900">We couldn't load your workspace</h1>
            <p className="mt-2 text-sm text-gray-500">
              Something went wrong reaching your workspace. Your data is safe —
              please try again.
            </p>
            <button type="button" onClick={() => load()} className={PRIMARY_BUTTON}>
              Try again
            </button>
            <p className="mt-4 text-sm text-gray-500">
              <Link to="/auth/login" className="font-medium text-emerald-700 hover:text-emerald-800">
                Sign in
              </Link>
            </p>
          </StateCard>
        )}
      </main>
    </BrandBackdrop>
  );
}

const PRIMARY_BUTTON =
  'mt-6 rounded-xl bg-emerald-600 px-6 py-2.5 font-semibold text-white shadow-lg shadow-emerald-600/25 transition-colors hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400';

function StateCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto mt-10 max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl shadow-black/40 ring-1 ring-black/5 sm:mt-16">
      {children}
    </div>
  );
}

/**
 * One app. Launchable → a real link into the suite app; when the app is
 * OIDC-handoff enabled (oidcLaunch) the click goes through the canonical
 * launch-intent flow, and the href stays the bare URL as a no-JS fallback
 * that is never inspected. Not launchable → a disabled card, never a dead link.
 * `featured` is the recommended app's large card.
 */
function AppTile({ app, featured = false }: { app: HomeApp; featured?: boolean }) {
  const canLaunch = app.launchable && !!app.launchUrl;
  const launchUrl = app.launchUrl as string;
  const onClick = (e: React.MouseEvent) => {
    if (!app.oidcLaunch) return;
    e.preventDefault();
    void launchSuiteApp(app.appId, { surface: 'home-launcher', fallbackUrl: launchUrl });
  };

  // Phase 2.x-c — the Application Registry v2 subtitle sits beneath the name
  // so users see the app's value without opening it.
  const subtitle = app.subtitle ? (
    <p
      className={featured ? 'mt-0.5 text-sm text-slate-300 sm:text-base' : 'mt-0.5 truncate text-sm text-gray-500'}
      data-testid={`tile-subtitle-${app.appId}`}
    >
      {app.subtitle}
    </p>
  ) : null;

  if (featured) {
    const body = (
      <>
        <AppLogo appId={app.appId} displayName={app.displayName} iconUrl={app.iconUrl} size="lg" className="shadow-lg shadow-black/30" />
        <div className="min-w-0 flex-1">
          <span className="inline-flex items-center rounded-full bg-emerald-400/15 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-300 ring-1 ring-emerald-400/30">
            Recommended
          </span>
          <h3 className="mt-2 text-xl font-semibold text-white sm:text-2xl">{app.displayName}</h3>
          {subtitle}
          {app.description && (
            <p className="mt-1.5 hidden text-sm leading-relaxed text-slate-400 line-clamp-2 sm:block">{app.description}</p>
          )}
        </div>
        {canLaunch ? (
          <span className="inline-flex w-full flex-none items-center justify-center gap-2 rounded-xl bg-emerald-500 px-5 py-3 text-sm font-semibold text-slate-950 shadow-lg shadow-emerald-500/25 transition-colors group-hover:bg-emerald-400 sm:w-auto">
            Open {app.displayName}
            <ArrowIcon className="h-4 w-4" />
          </span>
        ) : (
          <span className="flex-none text-sm text-slate-400">Currently unavailable</span>
        )}
      </>
    );
    const frame =
      'group flex flex-col items-start gap-4 rounded-3xl border border-white/10 bg-gradient-to-br from-emerald-500/20 via-white/[0.06] to-white/[0.02] p-5 shadow-2xl shadow-black/30 backdrop-blur sm:flex-row sm:items-center sm:gap-6 sm:p-7';
    return canLaunch ? (
      <a href={launchUrl} onClick={onClick} className={`${frame} transition-colors hover:border-emerald-400/40 focus:outline-none focus:ring-2 focus:ring-emerald-400`}>
        {body}
      </a>
    ) : (
      <div aria-disabled="true" className={`${frame} cursor-not-allowed opacity-70`}>
        {body}
      </div>
    );
  }

  const body = (
    <>
      <AppLogo appId={app.appId} displayName={app.displayName} iconUrl={app.iconUrl} size="md" />
      <div className="min-w-0 flex-1">
        <h3 className="truncate font-semibold text-gray-900">{app.displayName}</h3>
        {subtitle}
      </div>
    </>
  );
  if (canLaunch) {
    return (
      <a
        href={launchUrl}
        onClick={onClick}
        className="group flex items-center gap-3.5 rounded-2xl border border-gray-100 bg-white p-3.5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-emerald-400 sm:p-4"
      >
        {body}
        <span className="flex flex-none items-center gap-1 text-sm font-semibold text-emerald-700">
          <span aria-hidden="true">Open</span>
          <span className="sr-only">{`Open ${app.displayName}`}</span>
          <ArrowIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </span>
      </a>
    );
  }
  return (
    <div
      aria-disabled="true"
      className="flex cursor-not-allowed items-center gap-3.5 rounded-2xl border border-gray-100 bg-gray-50 p-3.5 opacity-60 sm:p-4"
    >
      {body}
      <span className="flex-none text-xs text-gray-400">Currently unavailable</span>
    </div>
  );
}

function NextStep({ to, title, sub, icon }: { to: string; title: string; sub: string; icon: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50/70 p-3 transition-colors hover:border-emerald-200 hover:bg-emerald-50/50 focus:outline-none focus:ring-2 focus:ring-emerald-400"
    >
      <span className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-white text-emerald-700 shadow-sm ring-1 ring-gray-200">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-gray-900">{title}</span>
        <span className="block truncate text-xs text-gray-500">{sub}</span>
      </span>
    </Link>
  );
}

function SparkIcon({ className }: { className: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function ArrowIcon({ className }: { className: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
    </svg>
  );
}

function GearIcon({ className }: { className: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function PeopleIcon({ className }: { className: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function CardIcon({ className }: { className: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );
}
